import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const RankingActivity = ({
  currentActivity,
  submitted,
  rankingSelections,
  setRankingSelections,
  handleOptionChange,
}) => {
  const isSubmitted = submitted?.isSubmitted || false;
  const isPreview = currentActivity?.ContentId === "preview"; // 🔥 detect preview

  // 🔥 Preview mode local state
  const [previewRanking, setPreviewRanking] = React.useState(rankingSelections);

  // Decide which array to render (preview or student mode)
  const displayedList = isPreview ? previewRanking : rankingSelections;

  const correctOrder = currentActivity?.Options?.map((o) => o.Value) || [];
  const userOrder = displayedList?.map((o) => o.Value) || [];

  const isCorrect = JSON.stringify(userOrder) === JSON.stringify(correctOrder);

  const resultBgColor = isSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  // ------ Handle Drag ------
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const updated = [...displayedList];
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    if (isPreview) {
      // 🔥 Only update preview state here
      setPreviewRanking(updated);
      return;
    }

    // Student mode
    setRankingSelections(updated);

    if (typeof handleOptionChange === "function") {
      handleOptionChange(
        currentActivity.ContentId,
        1,
        updated.map((x) => x.Value)
      );
    }
  };

  return (
    <>
      {/* ---------- DRAG UI ---------- */}
      <Box sx={{ padding: 2, mt: 2 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId={`ranking-${currentActivity.ContentId}`}
            direction="vertical"
            isDropDisabled={isSubmitted}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  border: "1px solid #ccc",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                }}
              >
                {displayedList.map((item, index) => (
                  <Draggable
                    key={String(item.Id)}
                    draggableId={String(item.Id)}
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          p: 1.5,
                          fontSize: "1.2rem",
                          boxShadow: 2,
                          cursor: isSubmitted ? "not-allowed" : "grab",
                        }}
                      >
                        {item.Value}
                      </Paper>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      {/* ---------- RESULT BOX + RATIONALE ---------- */}
      {isSubmitted && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              color: isCorrect ? "green" : "red",
            }}
          >
            {isCorrect ? "Correct" : "Incorrect"}
          </Typography>

          <Box sx={{ mt: 2 }}>
            {currentActivity?.Options?.map((opt, idx) => {
              const text = opt.Rationale?.Text?.trim();
              const cid = opt.Rationale?.ContentId;

              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    mt: 1,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    fontSize: "0.85rem",
                  }}
                >
                  {text ? (
                    <Typography>{`Rationale: ${text}`}</Typography>
                  ) : cid ? (
                    <Typography>{`Rationale ID: ${cid}`}</Typography>
                  ) : (
                    <Typography fontStyle="italic">
                      No rationale available.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default RankingActivity;
